import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  NgCommonModule,
  NgSharedModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  ReferralStatus,
  ReferralType,
} from '@principle-theorem/principle-core/interfaces';
import {
  ITimePeriod,
  getEnumValues,
  toTimestamp,
} from '@principle-theorem/shared';
import { debounceTime, map, take } from 'rxjs/operators';
import {
  IReferralFilters,
  ReferralListStore,
} from '../../stores/referral-list.store';
import { ReferralTypeChipComponent } from '../referral-type-chip/referral-type-chip.component';

type ReferralFiltersFormData = Pick<
  IReferralFilters,
  'searchTerm' | 'status' | 'type'
>;

@Component({
  selector: 'pr-referral-list-filters',
  imports: [
    NgCommonModule,
    MatBadgeModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    NgSharedModule,
    ReferralTypeChipComponent,
  ],
  templateUrl: './referral-list-filters.component.html',
  styleUrl: './referral-list-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralListFiltersComponent {
  private _store = inject(ReferralListStore);

  searchLabel = input('Search by ID, Patient, From, To');

  form = new TypedFormGroup<ReferralFiltersFormData>({
    searchTerm: new TypedFormControl<string>(''),
    status: new TypedFormControl<ReferralStatus[]>([]),
    type: new TypedFormControl<ReferralType[]>([]),
  });

  types = getEnumValues(ReferralType);
  statuses = getEnumValues(ReferralStatus);

  needsActionCount = toSignal(this._store.needsActionCount$, {
    initialValue: 0,
  });
  needsActionOnly = toSignal(this._store.needsActionOnly$, {
    initialValue: false,
  });

  filters = toSignal(this._store.filters$);

  hasActiveFilters = computed(() => {
    const currentFilters = this.filters();
    if (!currentFilters) {
      return false;
    }
    return (
      currentFilters.needsActionOnly ||
      currentFilters.searchTerm !== '' ||
      currentFilters.status.length > 0 ||
      currentFilters.type.length > 0 ||
      currentFilters.from !== undefined ||
      currentFilters.to !== undefined
    );
  });

  constructor() {
    this._store.filters$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((filters) => {
        this.form.patchValue(
          {
            status: filters.status,
            type: filters.type,
          },
          { emitEvent: false }
        );
      });

    this.form.valueChanges
      .pipe(
        debounceTime(250),
        map((value) => ({ ...value, type: value.type || [] })),
        takeUntilDestroyed()
      )
      .subscribe((value) => this._store.updateFilters(value));
  }

  updateDates(range: ITimePeriod): void {
    const filters = {
      from: toTimestamp(range.from.startOf('day')),
      to: toTimestamp(range.to.endOf('day')),
    };
    this._store.updateFilters(filters);
  }

  toggleNeedsAction(): void {
    this._store.setNeedsActionOnly(!this.needsActionOnly());
  }

  clearFilters(): void {
    this.form.reset({ emitEvent: false });
    this._store.resetFilters();
  }
}
